/**
 * The tutorial coach — a small banner over a guided first game that teaches the
 * one genuinely novel rule: royalty. The King can't be touched while its Prince
 * stands, so you must take the enemy Prince before you can mate their King. The
 * current step is *derived from game state* (not scripted moves), so it stays
 * correct however the learner plays.
 */
import { createSignal, Show } from "solid-js";
import { otherPlayer, pieceDef, royaltyStatus, type GameState, type PlayerId } from "../../core";
import type { GameController } from "../../app/controller";
import { Icon } from "./Icon";

type Step = "deploy" | "takePrince" | "mate" | "won" | "lost";

function currentStep(state: GameState, me: PlayerId): Step {
  if (state.phase === "finished") return state.winner === me ? "won" : "lost";
  const foe = otherPlayer(me);
  // Enemy Prince down → their King is exposed; go for mate.
  if (!royaltyStatus(state, foe).princeAlive) return "mate";
  // Have you put any piece of your own on the board yet (beyond your royals)?
  const deployed = state.board.some((p) => p && p.owner === me && !pieceDef(p.type).royal);
  return deployed ? "takePrince" : "deploy";
}

const COACH: Record<Step, { title: string; body: string }> = {
  deploy: {
    title: "Build your line",
    body: "You draw a card at the start of every turn. Tap a card in your hand, then a highlighted square in your two home ranks, to deploy a piece.",
  },
  takePrince: {
    title: "Hunt the Prince",
    body: "While the enemy Prince stands, their King can't be captured — and yours is safe while your Prince lives. Push forward and take their Prince first.",
  },
  mate: {
    title: "Now mate the King",
    body: "The enemy Prince has fallen, so their King is exposed. Corner it — checkmate wins the game.",
  },
  won: {
    title: "Checkmate — you win!",
    body: "Take the Prince, then mate the King: that's the whole game. You've got it.",
  },
  lost: {
    title: "Good try",
    body: "Your King was mated. The key: guard your Prince — once it falls, your King can be checkmated. Give it another go.",
  },
};

export function TutorialCoach(props: { controller: GameController }) {
  const [dismissed, setDismissed] = createSignal(false);
  const step = () => currentStep(props.controller.store.state, props.controller.mySeat ?? 0);
  const copy = () => COACH[step()];

  return (
    <Show when={!dismissed()}>
      {/* keyed on the step so it re-animates when the guidance changes */}
      <Show when={step()} keyed>
        <div class="coach" classList={{ done: step() === "won" || step() === "lost" }}>
          <span class="coach-icon"><Icon name="book" /></span>
          <div class="coach-text">
            <strong>{copy().title}</strong>
            <span>{copy().body}</span>
          </div>
          <button class="icon-link" title="Hide tips" onClick={() => setDismissed(true)}>
            <Icon name="close" />
          </button>
        </div>
      </Show>
    </Show>
  );
}
